import { Inject, Injectable, Optional } from '@angular/core';
import {
  CAMPAIGN_IDS_WITH_EXPIRATION_DATE,
  SUPERIOR_IDS_WITH_EXPIRATION_DATE
} from '@bcf-v2-configurators/tokens/affiliate/token';
import { IndexdbStorage } from '@bcf-v2-utilities/indexdb-storage/indexdb-storage';
import { addDays, isBefore } from 'date-fns';

type AffiliateDataRaw = string;

type AffiliateDataInMemory = {
  affData: AffiliateDataRaw;
  createdAt: Date;
};

const affiliateDataValidityInDays: number = 60;

const affiliateDataIndexdbStorageKey: string = 'affiliate-data';

enum AffiliateIdTier {
  Regular = 1,
  Superior = 2,
  Campaign = 3
}

@Injectable({ providedIn: 'root' })
export class AffiliateDataMemory {
  constructor(
    private _indexdbStorage: IndexdbStorage,
    @Optional()
    @Inject(SUPERIOR_IDS_WITH_EXPIRATION_DATE)
    private _superiorIdsWithExpirationDate?: number[],
    @Optional()
    @Inject(CAMPAIGN_IDS_WITH_EXPIRATION_DATE)
    private _campaignIdsWithExpirationDate?: number[]
  ) {}

  public async getDataRawFromMemory(): Promise<string | undefined> {
    const affiliateDataFromStorage: AffiliateDataInMemory | undefined =
      await this._indexdbStorage.get(affiliateDataIndexdbStorageKey);

    if (!affiliateDataFromStorage) {
      return undefined;
    }

    return affiliateDataFromStorage.affData;
  }

  public async updateDataRaw(affiliateData: string | undefined): Promise<void> {
    if (!affiliateData) {
      return;
    }

    const affiliateDataFromStorage: AffiliateDataInMemory | undefined =
      await this._indexdbStorage.get(affiliateDataIndexdbStorageKey);

    if (this._superiorIdsWithExpirationDate) {
      const incomingId: number | undefined = this._parseLeadingAffiliateId(affiliateData);
      if (
        incomingId !== undefined &&
        affiliateDataFromStorage &&
        this._isAffiliateRecordValid(affiliateDataFromStorage)
      ) {
        const storedId: number | undefined = this._parseLeadingAffiliateId(affiliateDataFromStorage.affData);
        if (
          storedId !== undefined &&
          this._getAffiliatePriorityTier(storedId) > AffiliateIdTier.Regular &&
          this._getAffiliatePriorityTier(incomingId) < this._getAffiliatePriorityTier(storedId)
        ) {
          return;
        }
      }
    } else if (this._isActiveInMemory(affiliateDataFromStorage)) {
      return;
    }

    await this._indexdbStorage.delete(affiliateDataIndexdbStorageKey);

    await this._indexdbStorage.set(affiliateDataIndexdbStorageKey, {
      affData: affiliateData,
      createdAt: new Date()
    });
  }

  private _isActiveInMemory(affiliateRecord: AffiliateDataInMemory | undefined): boolean {
    if (!affiliateRecord || !this._isAffiliateRecordValid(affiliateRecord)) {
      return false;
    }
    const affiliateId: number | undefined = this._parseLeadingAffiliateId(affiliateRecord.affData);
    return affiliateId !== undefined && Boolean(this._campaignIdsWithExpirationDate?.includes(affiliateId));
  }

  private _isAffiliateRecordValid(affiliateRecord: AffiliateDataInMemory | undefined): boolean {
    return Boolean(
      affiliateRecord && isBefore(new Date(), addDays(affiliateRecord.createdAt, affiliateDataValidityInDays))
    );
  }

  private _parseLeadingAffiliateId(raw: string): number | undefined {
    const parsed: number = Number(raw.split('_')[0]);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private _getAffiliatePriorityTier(affiliateId: number): AffiliateIdTier {
    if (this._campaignIdsWithExpirationDate?.includes(affiliateId)) {
      return AffiliateIdTier.Campaign;
    }
    if (this._superiorIdsWithExpirationDate?.includes(affiliateId)) {
      return AffiliateIdTier.Superior;
    }
    return AffiliateIdTier.Regular;
  }
}
